import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, NumberField, SaveBar, MissingConfigBanner } from '../components/Form';
import { useConfigDoc, useWriteConfig } from '../lib/configHooks';

// Geofence + distance-filter values are stored in meters but shown in feet.
const M_PER_FT = 0.3048;
const metersToFeet = (m?: number) =>
  typeof m === 'number' ? Math.round(m / M_PER_FT) : undefined;
const feetToMeters = (ft: number) => Math.round(ft * M_PER_FT);

interface OperationsConfig {
  [key: string]: unknown;
  jobOfferWindowSeconds?: number;
  notificationTtlHours?: number;
  scheduledReminderMinutes?: number[];
  missedPickupGraceMinutes?: number;
  geofenceRadiusMeters?: number;
  driverLocationIntervalSeconds?: number;
  driverLocationDistanceFilterMeters?: number;
  driverHeartbeatSeconds?: number;
}

export default function OperationsPage() {
  const { data } = useConfigDoc<OperationsConfig>('operations');
  const write = useWriteConfig('operations');
  const [draft, setDraft] = useState<OperationsConfig>({});
  const [dirty, setDirty] = useState(false);
  const [reminderText, setReminderText] = useState('');

  useEffect(() => {
    if (!dirty) {
      setDraft(data || {});
      setReminderText((data?.scheduledReminderMinutes || []).join(', '));
    }
  }, [data, dirty]);

  const set = <K extends keyof OperationsConfig>(
    k: K,
    v: OperationsConfig[K]
  ) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const onSave = async () => {
    const reminders = reminderText
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => b - a);
    await write.mutateAsync({ ...draft, scheduledReminderMinutes: reminders });
    setDirty(false);
  };

  return (
    <>
      <PageHeader
        title="Operations"
        subtitle="Timers and tolerances for the job lifecycle."
      />
      {data && Object.keys(data).length === 0 && (
        <MissingConfigBanner docIds={['operations']} />
      )}
      <Card title="Job offers & notifications">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="Job offer window"
            value={draft.jobOfferWindowSeconds}
            onChange={(v) => set('jobOfferWindowSeconds', v)}
            min={10}
            max={300}
            step={5}
            suffix="s"
          />
          <NumberField
            label="Notification TTL"
            value={draft.notificationTtlHours}
            onChange={(v) => set('notificationTtlHours', v)}
            min={1}
            max={168}
            step={1}
            suffix="hr"
          />
          <NumberField
            label="Missed-pickup grace"
            value={draft.missedPickupGraceMinutes}
            onChange={(v) => set('missedPickupGraceMinutes', v)}
            min={0}
            max={120}
            step={1}
            suffix="min"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm">
            <div className="mb-1 font-medium text-slate-700">
              Scheduled reminder offsets
            </div>
            <input
              type="text"
              value={reminderText}
              onChange={(e) => {
                setReminderText(e.target.value);
                setDirty(true);
              }}
              placeholder="60, 30, 15"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-500">
              Minutes before scheduled pickup to send reminders. Comma-separated.
            </p>
          </label>
        </div>
      </Card>
      <Card title="GPS & geofencing">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <NumberField
            label="Geofence radius"
            value={metersToFeet(draft.geofenceRadiusMeters)}
            onChange={(v) => set('geofenceRadiusMeters', feetToMeters(v))}
            min={50}
            max={3000}
            step={10}
            suffix="ft"
            hint="Arrival-detection radius."
          />
          <NumberField
            label="Location write interval"
            value={draft.driverLocationIntervalSeconds}
            onChange={(v) => set('driverLocationIntervalSeconds', v)}
            min={1}
            max={60}
            step={1}
            suffix="s"
          />
          <NumberField
            label="Distance filter"
            value={metersToFeet(draft.driverLocationDistanceFilterMeters)}
            onChange={(v) =>
              set('driverLocationDistanceFilterMeters', feetToMeters(v))
            }
            min={3}
            max={650}
            step={5}
            suffix="ft"
            hint="Min movement before a new GPS write."
          />
          <NumberField
            label="Heartbeat interval"
            value={draft.driverHeartbeatSeconds}
            onChange={(v) => set('driverHeartbeatSeconds', v)}
            min={5}
            max={120}
            step={1}
            suffix="s"
          />
        </div>
      </Card>
      <SaveBar
        dirty={dirty}
        saving={write.isPending}
        onSave={onSave}
        onReset={() => {
          setDraft(data || {});
          setReminderText((data?.scheduledReminderMinutes || []).join(', '));
          setDirty(false);
        }}
        warning="Lower GPS intervals increase battery drain and Firestore writes."
      />
    </>
  );
}
